import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  acceptanceTerms,
  banks,
  insurerBranches,
  insurers,
  policies,
  quoteFinancial,
  type OperationDetail,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatCivilDate, formatDateTime, formatMoney } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { buttonClassName } from '@/shared/ui/buttonStyles';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { RichTextView } from '@/shared/ui/RichTextEditor';
import { Select } from '@/shared/ui/Select';
import { useToast } from '@/shared/ui/useToast';

import { EndorsementIssuancePanel } from '../aftercare/EndorsementIssuancePanel';
import { BaixarApoliceButton } from '../portfolio/PolicyDocumentCard';
import { chaveDoFinanceiro, useFinanceiroDaCotacao } from './financeiroDaCotacao';

/**
 * Emissao da garantia a partir da operacao.
 *
 * E o passo que fecha a esteira: dele nascem a apolice e a comissao, na mesma
 * unidade de trabalho. Antes deste painel a operacao chegava a etapa de emissao e
 * parava, porque a rota existia sem nenhuma tela que a chamasse.
 */

/** Contraparte emissora: seguradora emite garantia, banco emite fianca. */
type Emissor = 'seguradora' | 'banco';

/**
 * A cotacao de endosso (P-END-1) nao cria apolice: a emissao dela aplica o endosso na
 * apolice endossada, com o painel proprio do endosso.
 */
export function IssuancePanel({ operacao }: { operacao: OperationDetail }) {
  if (operacao.proposal_type === 'endosso') {
    return <EndorsementIssuancePanel operacao={operacao} />;
  }
  return <PolicyIssuancePanel operacao={operacao} />;
}

function PolicyIssuancePanel({ operacao }: { operacao: OperationDetail }) {
  const { can } = useAuth();
  const [emitindo, setEmitindo] = useState(false);

  const podeEmitir = can('emissao.apolice.create');
  const naEtapa = operacao.stage === 'em_emissao';
  const jaEmitida = operacao.stage === 'emitida';

  // A apolice emitida e o resultado desta operacao, e a tela precisa levar ate
  // ela: emitir sem mostrar o que nasceu deixa a pessoa sem saber se funcionou. A
  // apolice e achada pela operacao, e nao entre as cem mais recentes da carteira
  // (inventario 47, secao 3.6).
  const carteira = useQuery({
    queryKey: ['policies', { operacao: operacao.id }],
    queryFn: ({ signal }) =>
      policies.list({ operation_id: operacao.id, limit: 1 }, signal),
    enabled: jaEmitida,
  });

  const apolice = (carteira.data?.items ?? []).find(
    (item) => item.operation_id === operacao.id,
  );

  // O que a emissao exige de documento, antes de pedir a emissao
  // (GetEmissionUploadDocumentStatus do OnPoint, P-APO-1).
  const pendencias = useQuery({
    queryKey: ['issuance-documents', operacao.id],
    queryFn: ({ signal }) => policies.issuanceDocuments(operacao.id, signal),
    enabled: naEtapa && can('emissao.apolice.view'),
  });
  const faltam = pendencias.data?.required ? pendencias.data.missing : [];

  if (!naEtapa && !jaEmitida) return null;

  return (
    <Card>
      <CardHeader
        title="Emissao"
        description="A emissao cria a apolice e lanca a comissao, juntas."
        action={
          podeEmitir && naEtapa ? (
            <Button onClick={() => setEmitindo(true)}>Emitir garantia</Button>
          ) : null
        }
      />

      <CardBody className="flex flex-col gap-3">
        {jaEmitida ? (
          apolice ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-ink-900">
                  Apolice {apolice.external_number || apolice.number}
                </p>
                <p className="text-2xs text-ink-500">
                  {apolice.issuer.label} · {formatMoney(apolice.insured_amount)} ·
                  vigencia ate {formatCivilDate(apolice.coverage_end)}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {/* O documento da apolice gerado na emissao (P-MIN-2). */}
                <BaixarApoliceButton policy={apolice} size="md" />
                <Link
                  to={`/apolices/${apolice.id}`}
                  className={buttonClassName({ variant: 'secondary' })}
                >
                  Ver na carteira
                </Link>
              </div>
              {apolice.policy_document?.status === 'nao_gerado' &&
              apolice.policy_document.reason ? (
                <p className="w-full text-2xs text-ink-500">
                  Documento da apolice nao gerado: {apolice.policy_document.reason}
                </p>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-ink-500">
              {carteira.isPending
                ? 'Carregando a apolice emitida.'
                : 'A operacao esta emitida. A apolice nao aparece na sua carteira visivel.'}
            </p>
          )
        ) : podeEmitir ? (
          <>
            <p className="text-sm text-ink-600">
              A operacao esta pronta para emissao. Informe a contraparte e a vigencia
              para gerar a apolice.
            </p>
            {faltam.length > 0 ? (
              <Alert tone="warning" title="Documentos que a emissao exige">
                Esta empresa so emite com os documentos obrigatorios da operacao
                recebidos ou dispensados. Faltam:{' '}
                {faltam.map((item) => item.title).join('; ')}.
              </Alert>
            ) : null}
          </>
        ) : (
          <p className="text-sm text-ink-500">
            A operacao esta pronta para emissao. Seu acesso nao permite emitir.
          </p>
        )}

        {/* Termo e declaracao da emissao (P-COT-4, com o termo de aceite do P-USU-2). */}
        <IssuanceTermsBlock operacao={operacao} emitida={jaEmitida} />
      </CardBody>

      {emitindo ? (
        <IssuanceDialog operacao={operacao} open onClose={() => setEmitindo(false)} />
      ) : null}
    </Card>
  );
}

/**
 * "Termo e declaracao" da emissao (P-COT-4): o termo de aceite vigente do tipo emissao
 * (P-USU-2), a caixa "Declaro que li e estou ciente das condicoes acima." e o aceite
 * gravado na cotacao, com a data, quem aceitou e a versao. A exigencia do aceite antes
 * da emissao e configuracao da empresa (EMI-001, nasce desligada).
 */
function IssuanceTermsBlock({
  operacao,
  emitida,
}: {
  operacao: OperationDetail;
  emitida: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [declarado, setDeclarado] = useState(false);
  const financeiro = useFinanceiroDaCotacao(operacao.id);
  const termo = useQuery({
    queryKey: ['acceptance-term', 'emissao'],
    queryFn: ({ signal }) => acceptanceTerms.current('emissao', signal),
    enabled: !emitida,
    retry: false,
  });

  const aceitar = useMutation({
    mutationFn: () => {
      if (!termo.data) throw new Error('Nenhum termo de aceite de emissao em vigor.');
      return quoteFinancial.acceptTerms(operacao.id, termo.data.id);
    },
    onSuccess: (dados) => {
      toast.success('Aceite do termo de emissao registrado.');
      queryClient.setQueryData(chaveDoFinanceiro(operacao.id), dados);
      setDeclarado(false);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (financeiro.isError && (emitida || termo.isError)) return null;
  const aceite = financeiro.data?.terms_acceptance;
  const exigido = financeiro.data?.issuance_requires_terms_acceptance === true;

  return (
    <div className="flex flex-col gap-2 rounded-md border border-line px-3 py-3">
      <p className="text-sm font-medium text-ink-900">Termo e declaracao</p>
      {aceite?.accepted ? (
        <p className="text-xs text-ink-600">
          Aceite registrado em {formatDateTime(aceite.accepted_at)}
          {aceite.accepted_label ? ` por ${aceite.accepted_label}` : ''}
          {aceite.version !== null ? ` (versao ${aceite.version})` : ''}.
        </p>
      ) : (
        <p className="text-xs text-ink-500">
          {exigido
            ? 'A empresa exige o aceite do termo de emissao antes de emitir.'
            : 'Sem aceite registrado.'}
        </p>
      )}
      {!emitida && termo.data ? (
        <>
          <p className="text-sm text-ink-900">{termo.data.title}</p>
          <div className="rolagem-fina max-h-48 overflow-y-auto rounded-lg border border-line bg-surface-sunken p-3">
            <RichTextView html={termo.data.body} />
          </div>
          <label className="flex items-center gap-2 text-sm text-ink-900">
            <input
              type="checkbox"
              checked={declarado}
              onChange={(event) => setDeclarado(event.target.checked)}
              className="size-4 rounded border-line-strong text-brand-500"
            />
            Declaro que li e estou ciente das condicoes acima.
          </label>
          <div>
            <Button
              variant="secondary"
              size="sm"
              disabled={!declarado}
              loading={aceitar.isPending}
              onClick={() => aceitar.mutate()}
            >
              Registrar aceite
            </Button>
          </div>
        </>
      ) : null}
      {!emitida && termo.isSuccess && termo.data === null ? (
        <p className="text-xs text-ink-500">
          Nenhum termo de aceite de emissao em vigor nesta empresa.
        </p>
      ) : null}
    </div>
  );
}

function IssuanceDialog({
  operacao,
  open,
  onClose,
}: {
  operacao: OperationDetail;
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  // O aceite exigido pela empresa (EMI-001): sem ele, o servidor recusa a emissao, e a
  // tela diz antes. Sem conseguir ler o financeiro, quem decide e o servidor.
  const financeiro = useFinanceiroDaCotacao(operacao.id);
  const faltaAceite =
    financeiro.data?.issuance_requires_terms_acceptance === true &&
    !financeiro.data.terms_acceptance.accepted;
  const parcelasDaCotacao = financeiro.data?.installments.items.length ?? 0;

  const [emissor, setEmissor] = useState<Emissor>('seguradora');
  const [contraparte, setContraparte] = useState('');
  // Filial da seguradora que emite (P-MIN-2), impressa em FILIAL. Opcional: como a
  // filial e escolhida no OnPoint nao tem fonte.
  const [filial, setFilial] = useState('');
  const [numeroExterno, setNumeroExterno] = useState('');
  const [referencia, setReferencia] = useState('');
  // Numero longo da apolice (P-APO-2): vem da seguradora, e o SeguraCore nao o gera.
  const [numeroLongo, setNumeroLongo] = useState('');

  // No modo automatico da configuracao da apolice, o numero e gerado na emissao e o
  // digitado seria recusado. Sem conseguir ler a configuracao, o campo fica.
  const configuracao = useQuery({
    queryKey: ['policy-settings'],
    queryFn: ({ signal }) => policies.settings(signal),
    retry: false,
  });
  const numeroGerado = configuracao.data?.number_mode === 'automatico';
  const [inicio, setInicio] = useState(operacao.coverage_start?.slice(0, 10) ?? '');
  const [fim, setFim] = useState(operacao.coverage_end?.slice(0, 10) ?? '');

  const seguradoras = useQuery({
    queryKey: ['insurers', { status: 'ativa' }],
    queryFn: ({ signal }) => insurers.list({ limit: 100 }, signal),
    enabled: emissor === 'seguradora',
  });

  const filiais = useQuery({
    queryKey: ['insurer-branches', contraparte, 'ativa'],
    queryFn: ({ signal }) => insurerBranches.list(contraparte, 'ativa', signal),
    enabled: emissor === 'seguradora' && contraparte !== '',
    retry: false,
  });

  const bancos = useQuery({
    queryKey: ['banks', { status: 'ativo' }],
    queryFn: ({ signal }) => banks.list({ limit: 100 }, signal),
    enabled: emissor === 'banco',
  });

  const emitir = useMutation({
    mutationFn: () =>
      policies.issue(operacao.id, {
        insurer_id: emissor === 'seguradora' ? contraparte : null,
        bank_id: emissor === 'banco' ? contraparte : null,
        external_number: numeroGerado ? '' : numeroExterno,
        coverage_start: inicio || null,
        coverage_end: fim || null,
        insurer_reference: referencia.trim(),
        long_number: numeroLongo.trim(),
        insurer_branch_id: emissor === 'seguradora' && filial !== '' ? filial : null,
      }),
    onSuccess: (apolice) => {
      // A operacao direta nao lanca comissao: nao ha corretor a quem repassar.
      toast.success(
        `Apolice ${apolice.external_number || apolice.number} emitida. ${
          operacao.broker ? 'Comissao lancada.' : 'Operacao direta: sem comissao.'
        }`,
      );
      void queryClient.invalidateQueries({ queryKey: ['operation'] });
      void queryClient.invalidateQueries({ queryKey: ['operations'] });
      void queryClient.invalidateQueries({ queryKey: ['policies'] });
      void queryClient.invalidateQueries({ queryKey: ['commissions'] });
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  // A lista so oferece contraparte em condicao de emitir. Oferecer uma inativa
  // levaria a pessoa ate a recusa do servidor sem motivo aparente.
  const disponiveis =
    emissor === 'seguradora'
      ? (seguradoras.data?.items ?? [])
          .filter((item) => item.status === 'ativa')
          .map((item) => ({ value: item.id, label: item.legal_name }))
      : (bancos.data?.items ?? [])
          .filter((item) => item.status === 'ativo')
          .map((item) => ({ value: item.id, label: item.name }));

  const carregando =
    emissor === 'seguradora' ? seguradoras.isPending : bancos.isPending;

  const faltaDado = contraparte === '' || inicio === '' || fim === '';
  const vigenciaInvertida = inicio !== '' && fim !== '' && fim < inicio;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Emitir garantia"
      description="A apolice e a comissao nascem juntas. Um estado pela metade seria impossivel de explicar depois."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Voltar
          </Button>
          <Button
            loading={emitir.isPending}
            disabled={faltaDado || vigenciaInvertida || faltaAceite}
            onClick={() => emitir.mutate()}
          >
            Emitir garantia
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {faltaAceite ? (
          <Alert tone="warning" title="Aceite do termo exigido">
            A empresa exige o aceite do termo de emissao antes de emitir. Registre o
            aceite no quadro Emissao.
          </Alert>
        ) : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Tipo de emissor"
            value={emissor}
            options={[
              { value: 'seguradora', label: 'Seguradora (seguro garantia)' },
              { value: 'banco', label: 'Banco (fianca bancaria)' },
            ]}
            onChange={(event) => {
              setEmissor(event.target.value as Emissor);
              setContraparte('');
            }}
          />
          <Select
            label="Contraparte"
            required
            placeholder={
              carregando ? 'Carregando...' : 'Selecione quem emite a garantia'
            }
            value={contraparte}
            options={disponiveis}
            hint="Sem contraparte a apolice nao tem quem a honre."
            onChange={(event) => {
              setContraparte(event.target.value);
              setFilial('');
            }}
          />
        </div>

        {emissor === 'seguradora' && contraparte !== '' ? (
          <Select
            label="Filial da seguradora"
            placeholder={
              filiais.isPending ? 'Carregando...' : 'Sem filial (o campo fica vazio)'
            }
            value={filial}
            options={(filiais.data ?? []).map((item) => ({
              value: item.id,
              label: item.city ? `${item.name} (${item.city})` : item.name,
            }))}
            hint="A filial sai no documento da apolice. Cadastre as filiais em Seguradoras."
            onChange={(event) => setFilial(event.target.value)}
          />
        ) : null}

        {!carregando && disponiveis.length === 0 ? (
          <Alert tone="info" title="Nenhuma contraparte ativa cadastrada">
            {emissor === 'seguradora'
              ? 'Cadastre e ative uma seguradora em Administracao para emitir seguro garantia.'
              : 'Cadastre e ative um banco em Administracao para emitir fianca bancaria.'}
          </Alert>
        ) : null}

        {numeroGerado ? (
          <p className="text-xs text-ink-600">
            O numero da apolice e gerado pelo SeguraCore na emissao, pela configuracao
            da apolice da empresa.
          </p>
        ) : (
          <Field
            label="Numero da apolice na contraparte"
            value={numeroExterno}
            hint="E o numero que aparece no documento e que o beneficiario cobra."
            onChange={(event) => setNumeroExterno(event.target.value)}
          />
        )}

        <Field
          label="Ref. da seguradora"
          value={referencia}
          maxLength={60}
          help="Codigo curto que a seguradora da a apolice, diferente do numero da apolice. Opcional; o que o codigo e esta a confirmar com a seguradora."
          onChange={(event) => setReferencia(event.target.value)}
        />

        <Field
          label="Numero longo da apolice"
          value={numeroLongo}
          maxLength={40}
          help="Numero longo que a seguradora da a apolice, com digitos separados por ponto, hifen, barra ou espaco. Opcional: sem ele, o campo fica vazio ate a seguradora informar. O SeguraCore nao gera esse numero."
          onChange={(event) => setNumeroLongo(event.target.value)}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Inicio da vigencia"
            type="date"
            required
            value={inicio}
            onChange={(event) => setInicio(event.target.value)}
          />
          <Field
            label="Fim da vigencia"
            type="date"
            required
            value={fim}
            error={
              vigenciaInvertida
                ? 'O fim da vigencia precisa ser posterior ao inicio.'
                : undefined
            }
            onChange={(event) => setFim(event.target.value)}
          />
        </div>

        <div className="rounded-lg border border-line bg-surface-sunken px-3 py-2">
          <p className="text-2xs font-semibold uppercase tracking-wide text-ink-500">
            O que sera gravado
          </p>
          <p className="mt-1 text-xs text-ink-700">
            Valor garantido {formatMoney(operacao.insured_amount)} e premio{' '}
            {formatMoney(operacao.premium)},{' '}
            {operacao.broker
              ? 'com comissao do corretor'
              : 'sem comissao (operacao direta, sem corretor)'}
            {parcelasDaCotacao > 0
              ? ` e as ${parcelasDaCotacao} parcela(s) informadas na cotacao`
              : ''}
            . Os valores vem da operacao e ficam gravados na apolice: uma alteracao
            futura na operacao nao reescreve o que foi emitido.
          </p>
        </div>
      </div>
    </Modal>
  );
}
